import { startTransition, useEffect, useRef, useState } from "react";
import LiquidGlass from "liquid-glass-react";
import { createLatestLoader, startVisiblePolling } from "./request-lifecycle.js";

const ACTIVE_STATES = new Set([
  "uploading",
  "identity_queued",
  "identity_scanning",
  "awaiting_player",
  "analysis_queued",
  "processing",
]);

const STATUS_COPY = {
  uploading: "Uploading game film",
  identity_queued: "Waiting for identity scan",
  identity_scanning: "Finding players and jersey numbers",
  awaiting_player: "Player confirmation needed",
  analysis_queued: "Queued for full analysis",
  processing: "Analyzing possessions and building clips",
  completed: "Report ready",
  failed: "Analysis needs attention",
  cancelled: "Analysis cancelled",
  expired: "Source video expired",
};

const DEMO_REPORT = {
  overall: 84,
  trackingConfidence: 0.91,
  trackedMinutes: 18.4,
  strengths: ["Consistent weak-side spacing", "Fast closeouts without over-running shooters", "Positive decisions after paint touches"],
  priorities: ["Tag the roller earlier in deep drop", "Replace two early-clock contested pull-ups", "Finish defensive possessions with a hit-first box out"],
  ratings: [
    { key: "shot_selection", label: "Shot selection", score: 82, confidence: 0.88, evidenceCount: 11 },
    { key: "off_ball", label: "Off-ball positioning", score: 91, confidence: 0.93, evidenceCount: 28 },
    { key: "playmaking", label: "Playmaking + security", score: 79, confidence: 0.84, evidenceCount: 17 },
    { key: "finishing", label: "Finishing + execution", score: 86, confidence: 0.9, evidenceCount: 9 },
    { key: "defensive_focus", label: "Defensive engagement", score: 88, confidence: 0.92, evidenceCount: 31 },
    { key: "on_ball_defense", label: "On-ball defense", score: 80, confidence: 0.82, evidenceCount: 13 },
    { key: "help_defense", label: "Help + rotations", score: 85, confidence: 0.87, evidenceCount: 19 },
    { key: "rebounding", label: "Rebounding + hustle", score: 76, confidence: 0.8, evidenceCount: 12 },
  ],
  events: [
    { id: "demo-1", timeSeconds: 194, type: "open_shot_made", title: "Corner three, made", importance: "The relocation keeps the passing window open and turns a drive into an uncontested catch-and-shoot attempt.", confidence: 0.96 },
    { id: "demo-2", timeSeconds: 387, type: "help_rotation", title: "Early nail help", importance: "You stop the first paint touch while staying close enough to recover to your shooter.", confidence: 0.89 },
    { id: "demo-3", timeSeconds: 612, type: "defensive_lapse", title: "Late roller tag", importance: "The low man rotates after the pocket pass, leaving no time to protect the rim or force the kick-out.", confidence: 0.83 },
    { id: "demo-4", timeSeconds: 841, type: "cut", title: "45-degree cut", importance: "The cut punishes a defender watching the ball and creates a clean finish before help can load up.", confidence: 0.94 },
  ],
};

function formatTime(seconds = 0) {
  const whole = Math.max(0, Math.round(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

function api(path, init) {
  return fetch(`/api/analyzer${path}`, {
    credentials: "same-origin",
    ...init,
    headers: {
      ...(init?.body instanceof Blob ? {} : { "content-type": "application/json" }),
      ...(init?.headers || {}),
    },
  }).then(async (response) => {
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.error || `Request failed (${response.status})`);
    return body;
  });
}

function Glass({ children, className = "", radius = 30, padding = "0", interactive = false, style, ...props }) {
  return (
    <div
      className={`glass-fallback ${interactive ? "is-interactive" : ""} ${className}`}
      style={{ "--glass-radius": `${radius}px`, "--glass-padding": padding, ...style }}
      {...props}
    >
      <div className="glass-content">{children}</div>
    </div>
  );
}

function LiquidLens() {
  return (
    <div className="liquid-lens" aria-hidden="true">
      <LiquidGlass
        glassSize={{ width: 132, height: 132 }}
        displacementScale={44}
        blurAmount={0.06}
        saturation={132}
        aberrationIntensity={1.6}
        elasticity={0.12}
        cornerRadius={66}
        padding="0"
        mode="standard"
      >
        <span className="liquid-lens-core" />
      </LiquidGlass>
    </div>
  );
}

function useScrollReveal(renderKey) {
  useEffect(() => {
    const root = document.documentElement;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    root.classList.add("reveal-ready");

    const observer = !reducedMotion && "IntersectionObserver" in window
      ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.14, rootMargin: "0px 0px -8%" })
      : null;

    function register(node) {
      if (!(node instanceof Element)) return;
      const targets = [
        ...(node.matches("[data-reveal]") ? [node] : []),
        ...node.querySelectorAll("[data-reveal]"),
      ];
      targets.forEach((target) => {
        if (reducedMotion || !observer) target.classList.add("is-visible");
        else observer.observe(target);
      });
    }

    register(document.body);
    const mutationObserver = new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach(register));
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer?.disconnect();
      mutationObserver.disconnect();
    };
  }, [renderKey]);
}

function Mark() {
  return <span className="mark" aria-hidden="true"><i /><i /><i /></span>;
}

function SetupNotice({ me }) {
  const missing = [];
  if (!me.databaseConfigured) missing.push("D1 database");
  if (!me.storageConfigured) missing.push("private R2 storage");
  if (!me.oauthConfigured) missing.push("Google OAuth");
  return (
    <Glass className="setup-notice" radius={22} padding="16px 18px">
      <div className="setup-row">
        <span className="pulse-dot" />
        <div>
          <strong>Private beta infrastructure is being connected.</strong>
          <p>{missing.length ? `${missing.join(", ")} ${missing.length === 1 ? "is" : "are"} not active yet.` : "All edge services are connected."} The product preview remains available below.</p>
        </div>
      </div>
    </Glass>
  );
}

function Landing({ me, onDemo }) {
  const canSignIn = me?.oauthConfigured && me?.databaseConfigured;
  return (
    <main id="analyzer-content" tabIndex={-1}>
      <section className="hero">
        <div className="court-lines" aria-hidden="true"><span /><span /><span /></div>
        <div className="orb orb-one" /><div className="orb orb-two" />
        <p className="kicker reveal-one">PLAYER-FIRST FILM INTELLIGENCE</p>
        <h1 className="reveal-two">See the game<br /><em>inside the game.</em></h1>
        <p className="hero-copy reveal-three">Upload a single-camera game. HoopVision follows one player through every possession, scores the decisions behind the box score, and cuts the moments worth teaching.</p>
        <div className="hero-actions reveal-four">
          <a className={`primary-action ${canSignIn ? "" : "is-disabled"}`} href={canSignIn ? "/api/analyzer/auth/google" : undefined} aria-disabled={!canSignIn}>
            <span className="google-g">G</span> Continue with Google
          </a>
          <button className="ghost-action" type="button" onClick={onDemo}>Explore a sample report <span>↗</span></button>
        </div>
        <div className="trust-line reveal-four"><span>Private video storage</span><span>Evidence, not guesses</span><span>Raw film deleted in 7 days</span></div>
      </section>

      <section className="process-section" id="how-it-works">
        <div className="section-label" data-reveal>HOW IT WORKS</div>
        <h2 data-reveal style={{ "--reveal-delay": "70ms" }}>One player. Every possession.<br />The context coaches need.</h2>
        <div className="process-grid">
          <article data-reveal style={{ "--reveal-delay": "80ms" }}><b>01</b><div className="process-icon scan-icon" /><h3>Upload the film</h3><p>Resumable uploads protect progress on game files up to 2 GB and 45 minutes.</p></article>
          <article data-reveal style={{ "--reveal-delay": "160ms" }}><b>02</b><div className="process-icon target-icon" /><h3>Confirm the player</h3><p>Choose from detected jerseys and team colors, then correct the court only when confidence is low.</p></article>
          <article data-reveal style={{ "--reveal-delay": "240ms" }}><b>03</b><div className="process-icon report-icon" /><h3>Coach from evidence</h3><p>Ratings connect to possession-level rules, synchronized events, and downloadable teaching clips.</p></article>
        </div>
      </section>

      <section className="signal-section" data-reveal>
        <Glass className="signal-card" radius={40} padding="clamp(28px, 6vw, 72px)">
          <div className="signal-copy"><p className="kicker">BUILT FOR HONEST FILM REVIEW</p><h2>Uncertain tracking never becomes a confident grade.</h2><p>HoopVision preserves frame-level confidence and excludes possessions it cannot support. When there is not enough film, the report says so.</p></div>
          <div className="signal-meter" aria-label="Example tracking confidence"><LiquidLens /><div><strong>91</strong><span>%</span></div><p>tracking confidence</p><i><span /></i></div>
        </Glass>
      </section>
    </main>
  );
}

function UploadPanel({ onUploaded, quota }) {
  const [file, setFile] = useState(null);
  const [permission, setPermission] = useState(false);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function durationOf(videoFile) {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      const objectUrl = URL.createObjectURL(videoFile);
      video.preload = "metadata";
      video.onloadedmetadata = () => { URL.revokeObjectURL(objectUrl); resolve(video.duration); };
      video.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error("The video metadata could not be read.")); };
      video.src = objectUrl;
    });
  }

  async function submit(event) {
    event.preventDefault();
    if (!file || !permission || busy) return;
    setBusy(true); setError(""); setProgress(1);
    try {
      if (file.size > 2 * 1024 ** 3) throw new Error("Choose a video smaller than 2 GB.");
      if (!/^(video\/(mp4|quicktime|webm))$/.test(file.type)) throw new Error("Use an MP4, MOV, or WebM file.");
      const durationSeconds = await durationOf(file);
      if (durationSeconds > 45 * 60) throw new Error("Choose a game that is 45 minutes or shorter.");
      const created = await api("/analyses", { method: "POST", body: JSON.stringify({ filename: file.name, sizeBytes: file.size, mimeType: file.type, durationSeconds, permissionConfirmed: true }) });
      const initiated = await api(`/analyses/${created.analysis.id}/upload/init`, { method: "POST", body: "{}" });
      const uploaded = new Map((initiated.parts || []).map((part) => [part.partNumber, part]));
      const parts = [];
      const partSize = initiated.partSize;
      const total = Math.ceil(file.size / partSize);
      for (let partNumber = 1; partNumber <= total; partNumber += 1) {
        if (uploaded.has(partNumber)) {
          parts.push(uploaded.get(partNumber));
        } else {
          const start = (partNumber - 1) * partSize;
          const response = await fetch(`/api/analyzer/analyses/${created.analysis.id}/upload/part?uploadId=${encodeURIComponent(initiated.uploadId)}&partNumber=${partNumber}`, { method: "PUT", credentials: "same-origin", headers: { "content-type": "application/octet-stream" }, body: file.slice(start, Math.min(file.size, start + partSize)) });
          const result = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(result.error || `Upload part ${partNumber} failed.`);
          parts.push({ partNumber, etag: result.etag });
        }
        setProgress(Math.round((partNumber / total) * 100));
      }
      await api(`/analyses/${created.analysis.id}/upload/complete`, { method: "POST", body: JSON.stringify({ uploadId: initiated.uploadId, parts }) });
      onUploaded(created.analysis.id);
    } catch (uploadError) {
      setError(uploadError.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Glass className="upload-card" radius={34} padding="clamp(22px, 5vw, 48px)" data-reveal>
      <form onSubmit={submit}>
        <div className="upload-heading"><div><p className="kicker">NEW ANALYSIS</p><h2>Drop in your game film.</h2></div><span className="quota-chip">{quota?.remaining ?? 2} of 2 games left</span></div>
        <label className={`drop-zone ${file ? "has-file" : ""}`}>
          <input type="file" accept="video/mp4,video/quicktime,video/webm" onChange={(event) => setFile(event.target.files?.[0] || null)} />
          <span className="upload-glyph">↑</span>
          <strong>{file ? file.name : "Choose an MP4, MOV, or WebM"}</strong>
          <small>{file ? `${(file.size / 1024 ** 2).toFixed(1)} MB ready to upload` : "Up to 2 GB · 45 minutes · 1080p"}</small>
        </label>
        <label className="permission-check"><input type="checkbox" checked={permission} onChange={(event) => setPermission(event.target.checked)} /><span>I confirm I have permission to upload and analyze this footage.</span></label>
        {busy && <div className="upload-progress"><i style={{ width: `${progress}%` }} /><span>{progress}%</span></div>}
        {error && <p className="form-error" role="alert">{error}</p>}
        <button className="primary-action full" disabled={!file || !permission || busy} type="submit">{busy ? "Uploading safely…" : "Start player scan"}</button>
      </form>
    </Glass>
  );
}

function StatusPanel({ analysis, onRefresh }) {
  const offline = analysis.machineOnline === false && ["identity_queued", "analysis_queued"].includes(analysis.status);
  return (
    <Glass className="status-panel" radius={34} padding="clamp(24px, 5vw, 46px)" data-reveal>
      <div className="status-top"><div><p className="kicker">ANALYSIS IN PROGRESS</p><h2>{STATUS_COPY[analysis.status] || "Working on your game"}</h2></div><span className={`state-pill ${offline ? "offline" : ""}`}>{offline ? "Analyzer offline, safely queued" : analysis.stage || "Securely processing"}</span></div>
      <div className="big-progress"><i style={{ width: `${analysis.progress || 0}%` }} /></div>
      <div className="progress-meta"><span>{analysis.progress || 0}% complete</span><span>{analysis.filename}</span></div>
      <div className="pipeline"><span className="done">Upload</span><span className={analysis.status !== "uploading" ? "done" : "active"}>Player scan</span><span className={["analysis_queued", "processing", "completed"].includes(analysis.status) ? "done" : ""}>Film analysis</span><span className={analysis.status === "completed" ? "done" : ""}>Report</span></div>
      <button className="ghost-action compact" type="button" onClick={onRefresh}>Refresh status</button>
    </Glass>
  );
}

function PlayerConfirm({ analysis, onConfirmed }) {
  const candidates = analysis.candidates || [];
  const [selected, setSelected] = useState(candidates[0]?.id || "");
  const candidate = candidates.find((item) => item.id === selected);
  const [jersey, setJersey] = useState(candidate?.jerseyNumber || "");
  const [teamColor, setTeamColor] = useState(candidate?.teamColor || "");
  const [position, setPosition] = useState("");
  const [level, setLevel] = useState("high_school");
  const [courtPoints, setCourtPoints] = useState([]);
  const [error, setError] = useState("");

  function addCourtPoint(event) {
    if (courtPoints.length >= 5) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    setCourtPoints((current) => [...current, {
      x: Number(((event.clientX - bounds.left) / bounds.width).toFixed(5)),
      y: Number(((event.clientY - bounds.top) / bounds.height).toFixed(5)),
    }]);
  }

  async function confirm() {
    try {
      const courtCalibration = analysis.needsCourtCalibration && courtPoints.length === 5
        ? { hoopCenter: courtPoints[0], courtPoints: courtPoints.slice(1), normalized: true }
        : null;
      await api(`/analyses/${analysis.id}/player`, { method: "POST", body: JSON.stringify({ candidateId: selected, jerseyNumber: jersey, teamColor, position, competitionLevel: level, courtCalibration }) });
      onConfirmed();
    } catch (requestError) { setError(requestError.message); }
  }

  return (
    <Glass className="confirm-card" radius={34} padding="clamp(22px, 5vw, 46px)" data-reveal>
      <p className="kicker">QUICK CONFIRMATION</p><h2>Who should HoopVision follow?</h2><p className="muted">Choose the player, then correct any detail that the identity scan missed.</p>
      <div className="candidate-grid">
        {candidates.map((item) => <button className={`candidate ${selected === item.id ? "selected" : ""}`} type="button" key={item.id} onClick={() => { setSelected(item.id); setJersey(item.jerseyNumber || ""); setTeamColor(item.teamColor || ""); }}><div className="candidate-photo">{item.thumbnailUrl ? <img src={item.thumbnailUrl} alt="" /> : <span style={{ background: item.teamColor || "#345" }}>{item.jerseyNumber || "?"}</span>}</div><strong>#{item.jerseyNumber || "?"}</strong><small>{Math.round((item.confidence || 0) * 100)}% identity confidence</small></button>)}
      </div>
      <div className="confirm-fields"><label>Jersey number<input value={jersey} onChange={(event) => setJersey(event.target.value)} maxLength={3} /></label><label>Team color<input value={teamColor} onChange={(event) => setTeamColor(event.target.value)} placeholder="Navy" /></label><label>Position<input value={position} onChange={(event) => setPosition(event.target.value)} placeholder="Guard / wing / big" /></label><label>Competition level<select value={level} onChange={(event) => setLevel(event.target.value)}><option value="youth">Youth</option><option value="high_school">High school</option><option value="college">College</option><option value="adult">Adult / club</option><option value="professional">Professional</option></select></label></div>
      {analysis.needsCourtCalibration && <div className="calibration-panel"><div className="calibration-note"><strong>Court check needed.</strong> Click the visible hoop center first, then four court intersections. Points use normalized coordinates so the mapping stays stable at every screen size.</div>{analysis.courtReferenceUrl ? <div className="court-picker" onClick={addCourtPoint} role="button" tabIndex="0" aria-label="Court calibration image"><img src={analysis.courtReferenceUrl} alt="Representative full-court frame for calibration" />{courtPoints.map((point, index) => <i key={`${point.x}-${point.y}`} style={{ left: `${point.x * 100}%`, top: `${point.y * 100}%` }}>{index === 0 ? "H" : index}</i>)}</div> : <p className="form-error">The analyzer did not provide a court reference frame.</p>}<div className="calibration-actions"><span>{courtPoints.length === 0 ? "Click the hoop center" : courtPoints.length < 5 ? `Add court point ${courtPoints.length} of 4` : "Court reference ready"}</span>{courtPoints.length > 0 && <button type="button" onClick={() => setCourtPoints([])}>Reset points</button>}</div></div>}
      {error && <p className="form-error">{error}</p>}
      <button className="primary-action" type="button" disabled={!selected || (analysis.needsCourtCalibration && courtPoints.length !== 5)} onClick={confirm}>Analyze this player</button>
    </Glass>
  );
}

function RatingCard({ rating, index }) {
  const unavailable = rating.score == null;
  return <article className="rating-card" data-reveal style={{ "--reveal-delay": `${(index % 2) * 80}ms` }}><div className="rating-ring" style={{ "--score": unavailable ? 0 : rating.score }}><strong>{unavailable ? "—" : rating.score}</strong></div><div><h3>{rating.label || rating.category}</h3><p>{unavailable ? "Insufficient evidence" : `${rating.evidenceCount} evidence moments · ${Math.round((rating.confidence || 0) * 100)}% confidence`}</p></div></article>;
}

function Report({ analysis, demo = false, onClose }) {
  const report = demo ? DEMO_REPORT : analysis.report;
  if (!report) return null;
  return (
    <main id="analyzer-content" className="report-shell" tabIndex={-1}>
      <div className="report-navigation"><button className="ghost-action compact" type="button" onClick={onClose}>{demo ? "Return to HoopVision" : "Back to game history"}</button>{demo && <span>Sample report</span>}</div>
      <section className="report-hero"><p className="kicker reveal-one">PLAYER REPORT · {demo ? "DEMO GAME" : analysis.filename}</p><div className="report-title reveal-two"><div><h1>Film grade<br /><em>{report.overall ?? "—"}</em></h1><p>{report.trackedMinutes?.toFixed?.(1) || report.trackedMinutes || 0} tracked minutes · {Math.round((report.trackingConfidence || 0) * 100)}% tracking confidence</p></div><div className="overall-orbit"><span>{report.overall ?? "—"}</span><small>OVERALL</small></div></div></section>
      <section className="report-section"><div className="section-label" data-reveal>CATEGORY RATINGS</div><div className="ratings-grid">{(report.ratings || []).map((rating, index) => <RatingCard key={rating.key || rating.category} rating={rating} index={index} />)}</div></section>
      <section className="report-section split-insights"><article data-reveal><p className="kicker">STRENGTHS</p>{(report.strengths || []).map((item, index) => <div className="insight" key={item}><b>{String(index + 1).padStart(2, "0")}</b><p>{item}</p></div>)}</article><article data-reveal style={{ "--reveal-delay": "100ms" }}><p className="kicker">NEXT FILM SESSION</p>{(report.priorities || []).map((item, index) => <div className="insight priority" key={item}><b>{String(index + 1).padStart(2, "0")}</b><p>{item}</p></div>)}</article></section>
      <section className="report-section"><div className="timeline-head" data-reveal><div><p className="kicker">EVIDENCE TIMELINE</p><h2>Moments worth teaching.</h2></div><span>{(report.events || []).length} selected clips</span></div><div className="event-list">{(report.events || []).map((event, index) => <article className="event-row" data-reveal style={{ "--reveal-delay": `${Math.min(index, 3) * 60}ms` }} key={event.id}><time>{formatTime(event.timeSeconds)}</time><div><span className={`event-type ${event.type?.includes("lapse") ? "negative" : ""}`}>{String(event.type || "moment").replaceAll("_", " ")}</span><h3>{event.title}</h3><p>{event.importance}</p><small>{Math.round((event.confidence || 0) * 100)}% evidence confidence</small></div>{!demo && event.clipId ? <a className="clip-button" href={`/api/analyzer/analyses/${analysis.id}/clips/${event.clipId}?download=1`}>Download clip ↓</a> : <button className="clip-button" disabled>Clip preview</button>}</article>)}</div></section>
    </main>
  );
}

function Dashboard({ me, onLogout }) {
  const [analyses, setAnalyses] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");
  const selectedLoader = useRef(null);
  if (!selectedLoader.current) {
    selectedLoader.current = createLatestLoader(
      (id, signal) => api(`/analyses/${id}`, { signal }),
      (result) => setSelected(result.analysis),
      (requestError) => setError(requestError.message),
    );
  }

  async function loadList() {
    try {
      const result = await api("/analyses");
      startTransition(() => setAnalyses(result.analyses || []));
      const active = result.analyses?.find((item) => ACTIVE_STATES.has(item.status));
      if (active) setSelectedId((current) => current || active.id);
    } catch (requestError) { setError(requestError.message); }
  }

  function loadSelected(id = selectedId) {
    if (id !== selectedId) return Promise.resolve();
    return selectedLoader.current.load(id);
  }

  useEffect(() => { loadList(); }, []);
  useEffect(() => {
    if (selectedId) loadSelected(selectedId);
    return () => selectedLoader.current.cancel();
  }, [selectedId]);
  useEffect(() => {
    if (!selected || selected.id !== selectedId || !ACTIVE_STATES.has(selected.status)) return undefined;
    return startVisiblePolling(() => loadSelected(selected.id), { onError: (requestError) => setError(requestError.message) });
  }, [selectedId, selected?.id, selected?.status]);

  if (selected?.status === "completed" && selected.report) return <Report analysis={selected} onClose={() => { setSelected(null); setSelectedId(null); }} />;

  return (
    <main id="analyzer-content" className="dashboard" tabIndex={-1}>
      <section className="dashboard-head reveal-two"><div><p className="kicker">YOUR FILM ROOM</p><h1>Welcome back, {me.user.name?.split(" ")[0] || "player"}.</h1><p>One active analysis at a time. Your source video disappears automatically after seven days.</p></div><button className="avatar-button" onClick={onLogout}>{me.user.avatarUrl ? <img src={me.user.avatarUrl} alt="" referrerPolicy="no-referrer" /> : <span>{me.user.name?.[0] || "H"}</span>}<small>Sign out</small></button></section>
      {error && <p className="form-error wide" role="alert">{error}</p>}
      {selected?.status === "awaiting_player" ? <PlayerConfirm analysis={selected} onConfirmed={() => loadSelected(selected.id)} /> : selected && ACTIVE_STATES.has(selected.status) && selected.status !== "uploading" ? <StatusPanel analysis={selected} onRefresh={() => loadSelected(selected.id)} /> : <UploadPanel quota={me.quota} onUploaded={(id) => { setSelectedId(id); loadList(); }} />}
      <section className="history" data-reveal><div className="history-head"><h2>Game history</h2><button onClick={loadList}>Refresh</button></div>{analyses.length ? analyses.map((analysis) => <button className="history-row" key={analysis.id} onClick={() => setSelectedId(analysis.id)}><span className={`history-state ${analysis.status}`} /><div><strong>{analysis.filename}</strong><small>{new Date(analysis.createdAt).toLocaleDateString()} · {STATUS_COPY[analysis.status]}</small></div><b>{analysis.report?.overall ?? (analysis.status === "completed" ? "Ready" : `${analysis.progress || 0}%`)}</b></button>) : <p className="empty-state">Your completed games and coaching reports will appear here.</p>}</section>
    </main>
  );
}

export default function App() {
  const [me, setMe] = useState(null);
  const [demo, setDemo] = useState(false);
  const [error, setError] = useState("");
  useScrollReveal(`${me ? "ready" : "loading"}:${me?.authenticated ? "member" : "guest"}:${demo ? "demo" : "app"}`);

  async function loadMe() {
    try { setMe(await api("/me")); } catch (requestError) { setError(requestError.message); setMe({ authenticated: false, databaseConfigured: false, storageConfigured: false, oauthConfigured: false }); }
  }

  useEffect(() => { loadMe(); }, []);

  async function logout() {
    try {
      await api("/logout", { method: "POST", body: "{}" });
      setMe((current) => ({ ...current, authenticated: false, user: null }));
      setError("");
    } catch (requestError) {
      setError("Sign out failed. Please try again.");
    }
  }

  if (demo) return <><SiteNav me={me} report /><Report demo analysis={{}} onClose={() => setDemo(false)} /></>;
  return (
    <div className="app-shell">
      <SiteNav me={me} />
      {me && (!me.databaseConfigured || !me.storageConfigured || !me.oauthConfigured) && <div className="notice-wrap"><SetupNotice me={me} /></div>}
      {error && <p className="global-error" role="alert">{error}</p>}
      {!me ? <main id="analyzer-content" className="loading-screen" tabIndex={-1} aria-busy="true"><Mark /><p role="status">Opening the film room…</p></main> : me.authenticated ? <Dashboard me={me} onLogout={logout} /> : <Landing me={me} onDemo={() => setDemo(true)} />}
      <footer><a href="/">Rui Wang</a><span>HoopVision private beta · Coaching estimate, not an official scouting product.</span><a href="https://github.com/flaggupray/hoopvision" target="_blank" rel="noreferrer">Engine notes ↗</a></footer>
    </div>
  );
}

function SiteNav({ me, report = false }) {
  return <><a className="skip-link" href="#analyzer-content">Skip to content</a><header className="site-nav"><a className="wordmark" href="/analyzer/"><Mark /><span>HoopVision</span><small>BETA</small></a><nav aria-label="Main navigation"><a className="site-home" href="/">Home</a><a href="/draft/">Draft</a><a href="/ideas/">Ideas</a>{!me?.authenticated && !report && <a className="nav-login" href={me?.oauthConfigured ? "/api/analyzer/auth/google" : "#how-it-works"}>{me?.oauthConfigured ? "Sign in" : "How it works"}</a>}</nav></header></>;
}
